import { useState } from "react";
import { User, Lock, Plus, Edit2, X, Eye, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Spinner } from "../ui/spinner";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogFooter } from "../ui/dialog";
import { InvestmentEvolutionChart } from "../InvestmentEvolutionChart";


interface Contribucion {
  id: string;
  totalContribucion: number;
  fechaContribucion: string;
  porcentajeAdicional: number;
  nota?: string;
  link?: string;
}

interface Inversor {
  id: string; // UUID from DB (users.id)
  nombre: string;
  usuario: string;
  contrasena: string;
  capitalInvertido: number;
  fechaInversion: string;
  porcentajeCapitalSocial: number;
  contribuciones?: Contribucion[];
}
interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingInversor: Inversor | null;
  newInversor: Partial<Inversor>;
  setNewInversor: (inversor: Partial<Inversor>) => void;
  newContribucion: {
    totalContribucion: string;
    fechaContribucion: string;
    porcentajeAdicional: string;
    nota?: string;
    link?: string;
  };
  setNewContribucion: (contribucion: any) => void;
  editingContribucionId: string | null;
  setEditingContribucionId: (id: string | null) => void;
  editingContribucionData: {
    totalContribucion: string;
    fechaContribucion: string;
    porcentajeAdicional: string;
    nota?: string;
    link?: string;
  };
  setEditingContribucionData: (data: any) => void;
  handleEditInversor: () => void;
  handleAddContribucion: () => void;
  handleEditContribucion: () => void;
  handleDeleteContribucion?: (id: string) => void;
  loadingContribs?: boolean;
  formatCurrency: (value: number) => string;
  formatPercentage: (value: number) => string;
}

export function AdminInversoresEditModal({
  isOpen,
  onClose,
  editingInversor,
  newInversor,
  setNewInversor,
  newContribucion,
  setNewContribucion,
  editingContribucionId,
  setEditingContribucionId,
  editingContribucionData,
  setEditingContribucionData,
  handleEditInversor,
  handleAddContribucion,
  handleEditContribucion,
  handleDeleteContribucion,
  loadingContribs = false,
  formatCurrency,
  formatPercentage
}: EditModalProps) {
  // Estado para controlar la expansión de la evolución de inversión
  const [isEvolutionExpanded, setIsEvolutionExpanded] = useState(false);

  // Calcular participación media

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[1400px] max-h-[94vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
        <div className="space-y-6 py-6 px-6">
          <div className="pb-4">
            <h3 className="text-2xl" style={{ fontWeight: 600 }}>Editar inversor</h3>
            <p className="text-sm text-gray-600 mt-2">Gestiona la información y contribuciones del inversor</p>
          </div>

          {/* DOS COLUMNAS */}
          <div className="flex flex-col sm:flex-row gap-8">

            {/* COLUMNA IZQUIERDA */}
              <div className="w-full sm:w-[320px] flex-shrink-0 flex flex-col gap-6">
              {/* Información del inversor */}
              <div className="flex flex-col gap-5">
                <h3 className="text-base text-[#111827]" style={{ fontWeight: 600 }}>Información del inversor</h3>
                
                {/* Nombre */}
                <div className="flex flex-col gap-1">
                  <Label className="text-sm block">Nombre completo *</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      className="mt-2 h-10 rounded-full bg-white border-0 pl-10 px-4 shadow-sm text-sm"
                      placeholder="Nombre y apellidos"
                      value={newInversor.nombre || ""}
                      onChange={(e) => setNewInversor({ ...newInversor, nombre: e.target.value })}
                    />
                  </div>
                </div>

                {/* Usuario */}
                <div className="flex flex-col gap-1">
                  <Label className="text-sm block">Usuario *</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      className="mt-2 h-10 rounded-full bg-white border-0 pl-10 px-4 shadow-sm text-sm"
                      placeholder="usuario@dominio"
                      value={newInversor.usuario || ""}
                      onChange={(e) => setNewInversor({ ...newInversor, usuario: e.target.value })}
                    />
                  </div>
                </div>

                {/* Contraseña */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm block">Contraseña</Label>
                    <span title="Cifrada — cambiar en Gestión de Inversores (candado)." className="text-xs text-gray-500">Confidencial</span>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="password"
                      disabled
                      readOnly
                      aria-disabled
                      className="mt-2 h-10 rounded-full bg-white border-0 pl-10 px-4 shadow-sm cursor-not-allowed"
                      placeholder="********"
                      value={newInversor.contrasena || ""}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 flex items-center gap-2">
                      <Eye size={16} className="text-gray-400 opacity-60" />
                      <Lock size={14} aria-label="Cambiar contraseña en Gestión de Inversores (icono de candado)" className="text-gray-400 cursor-default" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Cifrada — cambiar con el candado en Gestión de Inversores.</p>
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA */}
            <div className="flex-1 flex flex-col gap-8">

              {/* Gráfico de evolución de inversión - COLAPSABLE */}
              {editingInversor && (
                <div className="flex flex-col gap-4">
                  {/* Botón para expandir/colapsar */}
                  <button
                    onClick={() => setIsEvolutionExpanded(!isEvolutionExpanded)}
                    className="flex items-center justify-between w-full p-4 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors"
                    style={{ borderRadius: '12px' }}
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="text-base text-[#111827]" style={{ fontWeight: 600 }}>
                        Evolución de tu inversión
                      </h3>
                      <Badge 
                        className="text-xs px-2 py-0.5 bg-[#EEF2FF] text-[#4338CA] border-0"
                        style={{ fontWeight: 500, borderRadius: '6px' }}
                      >
                        Ver detalles
                      </Badge>
                    </div>
                    {isEvolutionExpanded ? (
                      <ChevronUp size={20} className="text-[#6B7280]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#6B7280]" />
                    )}
                  </button>

                  {/* Contenido colapsable */}
                  {isEvolutionExpanded && (
                    <div className="animate-in fade-in duration-200">
                      <InvestmentEvolutionChart investorId={editingInversor.id} />
                    </div>
                  )}
                </div>
              )}

              {/* Historial de contribuciones */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg text-[#111827]" style={{ fontWeight: 600 }}>Historial de contribuciones</h3>
                  {loadingContribs && (
                    <div className="flex items-center gap-2">
                      <Spinner size={14} />
                      <span className="text-sm text-[#6B7280]">Cargando...</span>
                    </div>
                  )}
                </div>
                
                {editingInversor ? (
                  <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-md overflow-hidden">
                    {/* Cabecera tabla */}
                    <div className="flex items-center py-4 px-4 border-b border-[#E5E7EB]">
                      <div className="w-[140px] text-xs text-[#4B5563] uppercase" style={{ fontWeight: 600 }}>
                        FECHA
                      </div>
                      <div className="w-[140px] text-xs text-[#4B5563] uppercase" style={{ fontWeight: 600 }}>
                        CANTIDAD (€)
                      </div>
                      <div className="w-[120px] text-xs text-[#4B5563] uppercase" style={{ fontWeight: 600 }}>
                        % CAPITAL
                      </div>
                        <div className="w-[240px] text-xs text-[#4B5563] uppercase px-4" style={{ fontWeight: 600 }}>
                          NOTA
                        </div>
                        <div className="w-[220px] text-xs text-[#4B5563] uppercase" style={{ fontWeight: 600 }}>
                          LINK
                        </div>
                      <div className="flex-1 text-xs text-[#4B5563] uppercase text-right" style={{ fontWeight: 600 }}>
                        ACCIONES
                      </div>
                    </div>

                    {/* Filas */}
                    <div className="divide-y divide-[#E5E7EB] px-4">
                      {/* Inversión Inicial */}
                {/* Inversión inicial removed — initial investment is not shown as a separate row */}

                      {/* Contribuciones adicionales */}
                      {editingInversor.contribuciones && editingInversor.contribuciones.map((contribucion) => (
                        <div key={contribucion.id} className="flex items-center py-3.5 hover:bg-gray-50 transition-colors">
                          {/* Fecha */}
                          <div className="w-[140px]">
                            {editingContribucionId === contribucion.id ? (
                              <Input 
                                type="date"
                                className="h-10 text-sm bg-white border-[#E5E7EB] rounded-lg"
                                value={editingContribucionData.fechaContribucion}
                                onChange={(e) => setEditingContribucionData({ 
                                  ...editingContribucionData, 
                                  fechaContribucion: e.target.value 
                                })}
                              />
                            ) : (
                              <span className="text-sm text-gray-700">
                                {contribucion.fechaContribucion ? new Date(contribucion.fechaContribucion).toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric' }) : '-'}
                              </span>
                            )}
                          </div>

                          {/* Cantidad */}
                          <div className="w-[140px]">
                            {editingContribucionId === contribucion.id ? (
                              <Input 
                                type="number"
                                className="h-10 text-sm bg-white border-[#E5E7EB] rounded-lg"
                                value={editingContribucionData.totalContribucion}
                                onChange={(e) => setEditingContribucionData({ 
                                  ...editingContribucionData, 
                                  totalContribucion: e.target.value 
                                })}
                              />
                            ) : (
                              <span className="text-sm text-gray-700" style={{ fontWeight: 500 }}>
                                {formatCurrency(contribucion.totalContribucion)}
                              </span>
                            )}
                          </div>

                          {/* Porcentaje */}
                          <div className="w-[120px]">
                            {editingContribucionId === contribucion.id ? (
                              <Input 
                                type="number"
                                step="0.1"
                                className="h-10 w-28 text-sm bg-white border-[#E5E7EB] rounded-lg"
                                value={editingContribucionData.porcentajeAdicional}
                                onChange={(e) => setEditingContribucionData({ 
                                  ...editingContribucionData, 
                                  porcentajeAdicional: e.target.value 
                                })}
                              />
                            ) : (
                              <Badge 
                                className={`text-sm px-4 py-1.5 border ${
                                  contribucion.porcentajeAdicional > 30 
                                    ? 'bg-[#ECFDF5] text-[#065F46] border-[#10B981]' 
                                    : contribucion.porcentajeAdicional > 20
                                    ? 'bg-[#FFFBEB] text-[#D97706] border-[#FCD34D]'
                                    : 'bg-[#FEE2E2] text-[#991B1B] border-[#EF4444]'
                                }`}
                                style={{ fontWeight: 600, borderRadius: '20px' }}
                              >
                                {formatPercentage(contribucion.porcentajeAdicional)}
                              </Badge>
                            )}
                          </div>

                            {/* Nota */}
                            <div className="w-[240px] px-4">
                              {editingContribucionId === contribucion.id ? (
                                <Input
                                  type="text"
                                  className="h-10 text-sm bg-white border-[#E5E7EB] rounded-lg"
                                  value={editingContribucionData.nota || ''}
                                  onChange={(e) => setEditingContribucionData({ ...editingContribucionData, nota: e.target.value })}
                                />
                              ) : (
                                <span className="text-sm text-gray-700 truncate">{contribucion.nota || '-'}</span>
                              )}
                            </div>

                            {/* Link */}
                            <div className="w-[220px] px-2">
                              {editingContribucionId === contribucion.id ? (
                                <Input
                                  type="text"
                                  className="h-10 text-sm bg-white border-[#E5E7EB] rounded-lg"
                                  value={editingContribucionData.link || ''}
                                  onChange={(e) => setEditingContribucionData({ ...editingContribucionData, link: e.target.value })}
                                />
                              ) : (
                                contribucion.link ? (
                                  (() => {
                                    const raw = String(contribucion.link || '').trim();
                                    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw);
                                    const safeHref = hasScheme || raw.startsWith('//') ? raw : `https://${raw}`;
                                    return (
                                      <a href={safeHref} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 truncate block">{contribucion.link}</a>
                                    );
                                  })()
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                )
                              )}
                            </div>

                          {/* Acciones */}
                          <div className="flex-1 flex items-center justify-end gap-3">
                            {editingContribucionId === contribucion.id ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="black"
                                  className="h-8 px-3 rounded-lg text-xs"
                                  onClick={handleEditContribucion}
                                  disabled={loadingContribs}
                                >
                                  Guardar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-3 rounded-lg text-xs border-[#E5E7EB]"
                                  onClick={() => {
                                    setEditingContribucionId(null);
                                    setEditingContribucionData({
                                      totalContribucion: "",
                                      fechaContribucion: "",
                                      porcentajeAdicional: "",
                                        nota: "",
                                        link: ""
                                    });
                                  }}
                                  disabled={loadingContribs}
                                >
                                  Cancelar
                                </Button>
                              </>
                            ) : (
                              <>
                                <button className="text-[#9CA3AF] hover:text-[#2B59FF] transition-colors" disabled={loadingContribs}>
                                  <Eye size={18} />
                                </button>
                                <button 
                                  className="text-[#9CA3AF] hover:text-[#2B59FF] transition-colors"
                                  onClick={() => {
                                    setEditingContribucionId(contribucion.id);
                                    setEditingContribucionData({
                                      totalContribucion: contribucion.totalContribucion.toString(),
                                      fechaContribucion: contribucion.fechaContribucion,
                                      porcentajeAdicional: contribucion.porcentajeAdicional.toString(),
                                      nota: contribucion.nota || '',
                                      link: contribucion.link || ''
                                    });
                                  }}
                                  disabled={loadingContribs}
                                >
                                  <Edit2 size={18} />
                                </button>
                                {handleDeleteContribucion && (
                                  <button 
                                    className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                                    onClick={() => handleDeleteContribucion(contribucion.id)}
                                    disabled={loadingContribs}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Total */}
                      <div className="border-t-2 border-[#D1D5DB]">
                        <div className="flex">
                          <div className="flex items-center bg-[#F9FAFB] px-4 py-4">
                            <div className="w-[140px] text-xs text-[#111827] uppercase" style={{ fontWeight: 700 }}>
                              TOTAL
                            </div>
                            <div className="w-[140px]">
                              <span className="text-sm text-[#111827]" style={{ fontWeight: 700 }}>
                                {formatCurrency(editingInversor.capitalInvertido)}
                              </span>
                            </div>
                            <div className="w-[120px]">
                              <span className="text-sm text-[#111827]" style={{ fontWeight: 700 }}>
                                {formatPercentage(editingInversor.porcentajeCapitalSocial)}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 px-4 py-4">
                            {/* actions column intentionally left plain (no background) */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#6B7280] italic py-4">No hay contribuciones</p>
                )}
              </div>

              {/* Añadir nueva contribución */}
              {editingInversor && (
                <div className="flex flex-col gap-4">
                  <h3 className="text-base text-[#111827] uppercase" style={{ fontWeight: 600, letterSpacing: '0.05em' }}>
                    Añadir nueva contribución
                  </h3>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      placeholder="0 €"
                      className="mt-2 h-10 w-[180px] text-sm rounded-full bg-white border-0 px-4 shadow-sm"
                      value={newContribucion.totalContribucion || ""}
                      onChange={(e) => setNewContribucion({ ...newContribucion, totalContribucion: e.target.value })}
                    />
                    <Input
                      type="date"
                      placeholder="dd/mm/aaaa"
                      className="mt-2 h-10 w-[160px] text-sm rounded-full bg-white border-0 px-4 shadow-sm"
                      value={newContribucion.fechaContribucion || ""}
                      onChange={(e) => setNewContribucion({ ...newContribucion, fechaContribucion: e.target.value })}
                    />
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="0.0 %"
                      className="mt-2 h-10 w-[120px] text-sm rounded-full bg-white border-0 px-4 shadow-sm"
                      value={newContribucion.porcentajeAdicional || ""}
                      onChange={(e) => setNewContribucion({ ...newContribucion, porcentajeAdicional: e.target.value })}
                    />
                    <Input
                      type="text"
                      placeholder="Link (opcional)"
                      className="mt-2 h-10 w-[240px] text-sm rounded-full bg-white border-0 px-4 shadow-sm"
                      value={newContribucion.link || ""}
                      onChange={(e) => setNewContribucion({ ...newContribucion, link: e.target.value })}
                    />
                    <Button
                      onClick={handleAddContribucion}
                      variant="black"
                      aria-label="Añadir contribución"
                      title="Añadir contribución"
                      className="h-10 px-4 rounded-md text-sm flex items-center gap-2"
                      disabled={loadingContribs || (!newContribucion.totalContribucion && !newContribucion.fechaContribucion && !newContribucion.porcentajeAdicional)}
                    >
                      <Plus size={16} strokeWidth={2} />
                      Añadir contribución
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* FOOTER */}
          <DialogFooter className="pt-6 border-t border-gray-100 flex justify-between items-center">
            <div>
              <Button variant="ghost" onClick={onClose} className="h-10 px-4">Cancelar</Button>
            </div>
            <div>
              <Button size="lg" variant="black" onClick={handleEditInversor} className="h-12 px-6 rounded-full" style={{ fontWeight: 500 }}>Guardar cambios</Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}